function isAccessory(variant) {
  return (variant.sku || '').includes('-AL-');
}

function variantDimensions(variant, kind) {
  if (kind === 'full') {
    const [color, style] = variant.options[0].split(' — ');
    return isAccessory(variant) ? [color, variant.options[1]] : [style, color, variant.options[1]];
  }
  if (kind === 'rear') return [variant.options[1], variant.options[2]];
  return variant.options;
}

function findVariant(variants, values, kind) {
  return variants.find((variant) => variantDimensions(variant, kind).every((value, index) => value === values[index]));
}

function formatMoney(cents, currency) {
  return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
}

function initializeGallery(root) {
  const stage = root.querySelector('[data-gallery-stage]');
  if (!stage) return () => {};
  const media = new Map();
  const initial = stage.querySelector(':scope > [data-media-id]');
  if (initial) media.set(initial.dataset.mediaId, initial.cloneNode(true));
  stage.querySelectorAll('template[data-media-template]').forEach((template) => {
    media.set(template.dataset.mediaTemplate, template.content.firstElementChild.cloneNode(true));
  });
  const links = [...root.querySelectorAll('[data-media-link]')];
  const show = (id) => {
    const content = media.get(String(id));
    if (!content || stage.firstElementChild?.dataset.mediaId === String(id)) return;
    stage.querySelectorAll('video').forEach((video) => video.pause());
    const next = content.cloneNode(true);
    next.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
      img.removeAttribute('fetchpriority');
    });
    stage.replaceChildren(next);
    links.forEach((link) => {
      if (link.dataset.mediaLink === String(id)) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  };
  links.forEach((link) => link.addEventListener('click', (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    show(link.dataset.mediaLink);
  }));
  return show;
}

function initializeProduct(root) {
  if (root.dataset.initialized) return;
  const script = root.querySelector('[data-product-data]');
  if (!script) return;
  const data = JSON.parse(script.textContent);
  const showMedia = initializeGallery(root);
  const kind = root.dataset.productKind;
  const currency = root.dataset.currency || 'USD';
  const input = root.querySelector('[data-variant-id]');
  const fallback = root.querySelector('[data-variant-fallback]');
  const enhanced = root.querySelector('[data-enhanced-options]');
  const form = input?.form;
  if (!input || !form) return;
  const labels = data.labels;
  const initial = data.variants.find((variant) => variant.id === data.initialId) || data.variants[0];
  const harness = kind === 'full' || kind === 'rear';
  let accessory = harness && isAccessory(initial);
  let currentVariant = initial;
  let values = variantDimensions(initial, kind).slice();
  let controls = [];
  const addButtons = [...root.querySelectorAll('[data-add-to-cart]')];
  const dynamicCheckout = root.querySelector('[data-dynamic-checkout]');
  const accessoryToggle = root.querySelector('[data-accessory-toggle]');
  const status = root.querySelector('[data-selection-status]');
  const priceNodes = root.querySelectorAll('[data-product-price]');
  const compare = root.querySelector('[data-compare-price]');
  const pool = () => data.variants.filter((variant) => !harness || isAccessory(variant) === accessory);
  const isSize = (name) => /size|weight/i.test(name);
  const names = () => kind === 'full'
    ? accessory ? [labels.color, labels.size] : [labels.style, labels.color, labels.size]
    : kind === 'rear' ? [labels.color, labels.size] : data.optionNames;
  if (!data.explicitVariant && fallback) {
    const sizeIndex = names().findIndex(isSize);
    if (sizeIndex >= 0) values[sizeIndex] = '';
  }

  function renderControls() {
    enhanced.replaceChildren();
    controls = names().map((name, index) => {
      const field = document.createElement('div');
      field.className = 'field';
      const label = document.createElement('label');
      const select = document.createElement('select');
      select.id = 'Choice-' + root.dataset.sectionId + '-' + index;
      label.htmlFor = select.id;
      label.textContent = name;
      select.required = true;
      select.dataset.choiceIndex = index;
      field.append(label, select);
      enhanced.append(field);
      select.addEventListener('change', () => {
        values[index] = select.value;
        refreshOptions(index);
        update(true);
      });
      return select;
    });
    refreshOptions(-1);
  }

  function refreshOptions(changedIndex) {
    const candidates = pool();
    controls.forEach((select, index) => {
      const possible = candidates.filter((variant) => {
        const dimensions = variantDimensions(variant, kind);
        return dimensions.slice(0, index).every((value, prior) => !values[prior] || values[prior] === value);
      });
      const options = [...new Set(possible.map((variant) => variantDimensions(variant, kind)[index]))];
      const requireChoice = isSize(names()[index]);
      if (!options.includes(values[index])) values[index] = requireChoice ? '' : options[0] || '';
      select.replaceChildren();
      if (requireChoice) {
        const placeholder = new Option(labels.selectSize, '');
        select.add(placeholder);
      }
      options.forEach((value) => {
        const available = possible.some((variant) => variantDimensions(variant, kind)[index] === value && variant.available);
        const option = new Option(value + (available ? '' : ' — ' + labels.sold), value);
        option.disabled = !available;
        select.add(option);
      });
      select.value = values[index];
    });
  }

  function update(changeImage) {
    const candidates = pool();
    const complete = values.every(Boolean);
    const variant = complete ? findVariant(candidates, values, kind) : null;
    currentVariant = variant;
    input.value = variant ? String(variant.id) : '';
    input.dispatchEvent(new Event('change', { bubbles: true }));
    const available = Boolean(variant?.available);
    addButtons.forEach((button) => {
      button.disabled = !available;
      button.textContent = !complete ? labels.selectAll : available ? labels.add : variant ? labels.sold : labels.unavailable;
    });
    if (dynamicCheckout) dynamicCheckout.hidden = !available;
    const displayVariant = variant || candidates.find((candidate) => {
      const dimensions = variantDimensions(candidate, kind);
      return dimensions.every((value, index) => !values[index] || values[index] === value);
    });
    if (displayVariant) {
      priceNodes.forEach((node) => { node.textContent = formatMoney(displayVariant.price, currency); });
      if (compare) {
        compare.hidden = !variant || !(variant.compare_at_price > variant.price);
        compare.textContent = !compare.hidden ? formatMoney(variant.compare_at_price, currency) : '';
      }
    }
    if (status) {
      if (variant) {
        status.textContent = variant.title;
        status.dataset.state = 'ok';
      } else if (status.dataset.state !== 'attention') {
        status.textContent = '';
      }
    }
    if (harness) {
      root.querySelectorAll('[data-product-title]').forEach((node) => {
        node.textContent = accessory ? labels.accessoryTitle : data.title;
      });
      root.querySelectorAll('[data-harness-copy]').forEach((node) => { node.hidden = accessory; });
      document.querySelectorAll(`[data-product-story-kind="${kind}"]`).forEach((node) => { node.hidden = accessory; });
      const caption = root.querySelector('[data-gallery-caption]');
      if (caption) caption.textContent = accessory ? labels.galleryAccessory : labels.galleryHarness;
      const included = root.querySelector('[data-included]');
      if (included) included.textContent = accessory ? labels.accessoryNote : data.included;
      if (accessoryToggle) accessoryToggle.textContent = accessory ? labels.backHarness : labels.accessoryLink;
    }
    if (changeImage) {
      if (displayVariant?.mediaId) showMedia(displayVariant.mediaId);
      const url = new URL(location.href);
      if (variant) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      history.replaceState({}, '', url);
    }
  }

  if (fallback && enhanced) {
    renderControls();
    update(false);
    fallback.hidden = true;
    enhanced.hidden = false;
  }
  if (accessoryToggle && data.variants.some(isAccessory)) {
    accessoryToggle.hidden = false;
    accessoryToggle.addEventListener('click', () => {
      accessory = !accessory;
      const next = pool().find((variant) => variant.available) || pool()[0];
      values = variantDimensions(next, kind).slice();
      const sizeIndex = names().findIndex(isSize);
      if (sizeIndex >= 0) values[sizeIndex] = '';
      renderControls();
      update(true);
      controls[0]?.focus();
    });
  }
  function nudgeMissing() {
    const missing = controls.find((select) => !select.value);
    if (missing) {
      missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
      missing.focus({ preventScroll: true });
      missing.classList.add('is-missing');
    }
    if (status) {
      status.textContent = missing ? (labels.chooseFirst || labels.selectAll) : (labels.chooseAll || labels.selectAll);
      status.dataset.state = 'attention';
    }
  }

  form.addEventListener('submit', (event) => {
    if (!currentVariant?.available) {
      event.preventDefault();
      nudgeMissing();
    }
  });

  const buybarButton = root.querySelector('[data-buybar-add]');
  if (buybarButton) {
    buybarButton.addEventListener('click', () => {
      if (currentVariant?.available) {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else addButtons[0]?.click();
      } else {
        nudgeMissing();
      }
    });
  }
  root.dataset.initialized = 'true';
}

if (typeof document !== 'undefined') {
  const initialize = (scope) => {
    scope.querySelectorAll('[data-product-root]').forEach((root) => {
      try { initializeProduct(root); } catch (error) { console.error('Product options could not be enhanced.', error); }
    });
  };
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
}
if (typeof module !== 'undefined') module.exports = { findVariant, variantDimensions, isAccessory };
